import { Link } from "react-router";
import { ROUTES, SECURITY_EMAIL } from "../links";
import { SecFlow } from "../widgets/SecFlow";
import { usePageTitle } from "./usePageTitle";
import { PageFaq } from "../widgets/PageFaq";

const ROWS: [string, string][] = [
  [
    "Connections are read-only",
    "When you link an institution, the permission granted is to read balances and transaction history. There is no code path in this product that can initiate a transfer, a payment or a trade. Not for us, not for Abi, not for anyone who got hold of your account.",
  ],
  [
    "We never see your banking password",
    "Banking connections run through Flinks and investment connections through Wealthica, both regulated Canadian aggregators. You enter your credentials on their screen, never ours, and we receive a token that reads data and nothing else.",
  ],
  [
    "256-bit AES encryption at rest, TLS in transit",
    "Sensitive data is encrypted in storage and everything moving between your browser and our servers is encrypted in transit.",
  ],
  [
    "Disconnect an institution and its data goes with it",
    "Removing a connection removes the data it brought. Deleting your account removes the rest.",
  ],
  [
    "Your data is held in Canada",
    "Stored and processed on Canadian infrastructure, under Canadian privacy law. We are a Burlington, Ontario company and your financial picture does not leave the country.",
  ],
  [
    "We don&rsquo;t sell your data and we don&rsquo;t run ads",
    "Your financial picture is never sold, rented or used to target you. The subscription is what keeps the lights on.",
  ],
  [
    "You can see what we hold",
    "Every figure in your plan is listed on one page inside the product, grouped by where it came from &mdash; read from your bank, confirmed by you, typed by you, or assumed by the engine.",
  ],
];

/** Security (the prototype's `data-s="security"`). */
export function SecurityPage() {
  usePageTitle("Security");
  return (
    <div className="spage on" data-s="security">
      <header className="hero" style={{ paddingBottom: 0 }}>
        <div className="wrap">
          <h1>
            We read your accounts. We <em>never</em> move money.
          </h1>
          <p className="lede">
            You&rsquo;re about to hand a piece of software the clearest picture of your finances
            that exists anywhere. Here is exactly what happens to it.
          </p>
        </div>
      </header>
      <section className="band">
        <div className="wrap" style={{ maxWidth: 860 }}>
          <div className="card tall" style={{ marginBottom: 16 }}>
            <SecFlow id="secflow2" />
          </div>
          <div className="card tall">
            {ROWS.map(([b, p]) => (
              <div className="secrow" key={b}>
                <span className="ic">&#8226;</span>
                <div>
                  <b dangerouslySetInnerHTML={{ __html: b }} />
                  <p dangerouslySetInnerHTML={{ __html: p }} />
                </div>
              </div>
            ))}
          </div>
          <p className="fcap" style={{ marginTop: 22 }}>
            Abi produces projections and recommendations. It is not a licensed investment dealer and
            nothing it produces is a solicitation to buy or sell a security. Where a recommendation
            touches insurance or investments, we say what assumption it rests on.
          </p>
          <div className="card tall" style={{ marginTop: 16 }}>
            <p className="mono" style={{ marginBottom: 14 }}>
              Where to reach us
            </p>
            <div className="secrow" style={{ paddingTop: 0 }}>
              <span className="ic">&#8226;</span>
              <div>
                <b>Security questions</b>
                <p>
                  <a
                    href={`mailto:${SECURITY_EMAIL}`}
                    style={{
                      color: "var(--s-gold)",
                      textDecoration: "none",
                      borderBottom: "1px solid rgba(242,185,13,.3)",
                    }}
                  >
                    {SECURITY_EMAIL}
                  </a>{" "}
                  &mdash; reviewed by a person, not a queue.
                </p>
              </div>
            </div>
            <div className="secrow">
              <span className="ic">&#8226;</span>
              <div>
                <b>WealthyPlanet Inc.</b>
                <p>
                  Burlington, Ontario, Canada. Registered and operating here, with your data held
                  here.
                </p>
              </div>
            </div>
            <div className="secrow">
              <span className="ic">&#8226;</span>
              <div>
                <b>Independent attestation</b>
                <p>
                  We do not hold a SOC 2 report today and we are not going to imply otherwise.
                  Flinks and Wealthica, who handle the account connections themselves, publish their
                  own certifications. Ask us where we are and we will tell you.
                </p>
              </div>
            </div>
          </div>
          <div style={{ textAlign: "center", marginTop: 40 }}>
            <Link className="sbtn p big" to={ROUTES.start}>
              Start your plan
            </Link>
            <p className="micro">Read-only from the first click.</p>
          </div>
        </div>
      </section>
      <PageFaq page="security" />
    </div>
  );
}
